import React from 'react';
import Card from 'Components/DesignSystem/Card/Card';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { EditIcon } from 'assests/GmsIcons';

export type DetailSectionConfig = {
    type: 'party' | 'domain' | 'channel';
    data: {
        title: string;
        partyName?: string; 
        partyType?: string; 
        address?: string; 
        description?: string; 
    };
    onEdit?: () => void;
};

/**
 * The party/domain/channel summary card on the DS `Card` (§ 119). Shared by
 * `ManageDomain` and `ManageChannel`, so this change is visible on both screens.
 *
 * Two real defects the hand-rolled card carried:
 *
 * - **A hardcoded `tw-h-[168px]`.** The card was a fixed height regardless of content:
 *   dead space below a short address, and an overflowing description on a long one —
 *   there was no `overflow` handling either, so the text simply escaped the tinted box.
 *   Height is now content-driven.
 * - **The edit control was a `<div role="button">` with no `tabIndex`.** It announced
 *   itself as a button and could not be reached or activated from the keyboard, which
 *   made Edit Party (and Edit Domain, on the Channel screen) **mouse-only** — the
 *   classic SC 2.1.1 failure. It is a real `<button>` now, so Tab, Enter and Space all
 *   work, and it gets a focus ring it never had.
 *
 * The label/value pairs keep the same reading order and the same conditional logic,
 * including the `type !== 'party' && data.partyName` and `type === 'party' &&
 * data.partyType` gates and the Address/Description swap — every one of which a test
 * pins. Labels move to `sub-title-label` over `caption-metadata` ink rather than the
 * raw `#969fae`/`text-xs` pair, which was off-palette in both directions.
 */
const DetailSection: React.FC<{ config: DetailSectionConfig }> = ({ config }) => {
    const { type, data, onEdit } = config;

    return (
        <Card
            elevation="none"
            className="tw-mt-4 tw-bg-surface-sunken"
            data-testid="pm-detail-section"
        >
            <div className="tw-flex tw-justify-between tw-items-start tw-gap-2.5">
                {/* `heading-1`, not the old `text-[24px]`: 24px semibold is not on the
                    type scale, and this is the page's own subject heading. */}
                <Typography variant="heading-1" as="h2" className="tw-text-ink" data-testid="pm-detail-section-title">
                    {data.title}
                </Typography>
                {onEdit && (
                    <Button
                        variant="subtle"
                        size="sm"
                        aria-label="Edit"
                        /* The legacy testid is kept verbatim. It is auto-generated and
                           names the wrong element type now, but specs key off it. */
                        data-testid="domain-detailsection-div-1"
                        onClick={onEdit}
                    >
                        <EditIcon />
                    </Button>
                )}
            </div>

            {type !== 'party' && data.partyName && (
                <Field label="Party" value={data.partyName} testid="pm-detail-section-party" />
            )}

            {type === 'party' && data.partyType && (
                <Field label="Party Type" value={data.partyType} testid="pm-detail-section-party-type" />
            )}

            <Field
                label={type === 'party' ? 'Address' : 'Description'}
                value={type === 'party' ? data.address : data.description}
                testid={type === 'party' ? 'pm-detail-section-address' : 'pm-detail-section-description'}
            />
        </Card>
    );
}

/** One label/value pair. Extracted only because the card renders three of them. */
const Field = ({ label, value, testid }: { label: string; value?: string; testid: string }) => (
    <div className="tw-mt-4 tw-px-1">
        <Typography variant="sub-title-label" as="div" className="tw-text-ink-tertiary">
            {label}
        </Typography>
        <Typography variant="title" as="div" className="tw-text-ink tw-mt-1" data-testid={testid}>
            {value}
        </Typography>
    </div>
);

export default DetailSection;
